import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import axios from "axios";

// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import StatusBadge from "components/ReportUI/Statusbadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";

import IgnitionReportsTable from "./ignitionReportTable";
import { ignitionReportEndpoint } from "config";

function IgnitionReport() {
  // New State for Toggle and Trip Data
  const [viewType, setViewType] = useState("report");
  const [tripData, setTripData] = useState([]);

  // Existing States
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicleId, setVehicleId] = useState("");

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  const today = new Date();
  const startTimeDefault = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
  const endTimeDefault = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

  const [startTime, setStartTime] = useState(startTimeDefault);
  const [endTime, setEndTime] = useState(endTimeDefault);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Table columns for Ignition Report
  const reportColumns = [
    { Header: "Date", accessor: "onDate", width: "10%" },
    {
      Header: "Ignition Status",
      accessor: "ignitionStatus",
      width: "10%",
      Cell: ({ value }) => (
        <StatusBadge value={value} trueLabel="ON" falseLabel="OFF" trueValues={["on", "true", "1", "running"]} />
      ),
    },
    { Header: "Start Time", accessor: "ignStartTimeDate", width: "15%" },
    { Header: "Start Location", accessor: "startLocation", width: "15%" },
    { Header: "End Time", accessor: "ignEndTimeDate", width: "15%" },
    { Header: "End Location", accessor: "endLocation", width: "15%" },
    { Header: "Time Between (Hr:Min:Sec)", accessor: "ignChangeTime", width: "15%" },
    { Header: "Total Distance (m)", accessor: "totalDistance", width: "10%" },
  ];

  // Table columns for Trip Report
  const tripColumns = [
    { Header: "Trip ID", accessor: "id", width: "10%" },
    { Header: "Start Time", accessor: "startTime", width: "20%" },
    { Header: "Start Location", accessor: "startLocation", width: "25%" },
    { Header: "End Time", accessor: "endTime", width: "20%" },
    { Header: "End Location", accessor: "endLocation", width: "25%" },
  ];

  // Map Link component helper
  const renderMapLink = (lat, lng, label, colorClass) => (
    <MDBox display="flex" alignItems="center">
      <LocationOnIcon fontSize="small" sx={{ color: `${colorClass}.main`, mr: 0.5 }} />
      <a
        href={`https://www.google.com/maps?q=$${lat},${lng}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1a73e8", fontSize: "0.85rem", fontWeight: "bold" }}
      >
        {label}
      </a>
    </MDBox>
  );

  // Format Date for Trip API (YYYY-MM-DD)
  const formatDateYYYYMMDD = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Fetch Existing Ignition Report
  const fetchReport = async () => {
    const token = localStorage.getItem("token");
    const formatDateTime = (d) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const formatDate = (d) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    };

    try {
      const data = {
        startTime: formatDateTime(startTime),
        endTime: formatDateTime(endTime),
        zone: "Asia/Kolkata",
        things: vehicleId,
        thingIds: [vehicleId],
        date: formatDate(startTime),
      };

      const config = {
        method: "post",
        url: ignitionReportEndpoint,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      
      const formattedRows = response.data.map((log) => ({
        onDate: log.onDate,
        ignitionStatus: log.ignitionStatus,
        ignStartTimeDate: log.ignStartTimeDate,
        rawStartLat: log.ignStartLatitude,
        rawStartLng: log.ignStartLongitude,
        startLocation: renderMapLink(log.ignStartLatitude, log.ignStartLongitude, "View Start", "info"),
        ignEndTimeDate: log.ignEndTimeDate,
        rawEndLat: log.ignEndLatitude,
        rawEndLng: log.ignEndLongitude,
        endLocation: renderMapLink(log.ignEndLatitude, log.ignEndLongitude, "View End", "error"),
        ignChangeTime: log.ignChangeTime,
        totalDistance: log.totalDistance,
      }));

      setReportData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading ignition report");
      openErrorSB();
    }
  };

  // Fetch New Trip Report
  const fetchTripReport = async () => {
    const token = localStorage.getItem("token");
    try {
      const data = {
        startDate: formatDateYYYYMMDD(startTime),
        endDate: formatDateYYYYMMDD(endTime),
      };

      const config = {
        method: "post",
        url: `https://v3.dhundhoo.com/reports/ignitionTripReport/${vehicleId}`,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      const resData = response.data || {};

      // Convert Object {"1": {...}, "2": {...}} to Array
      const formattedTrips = Object.keys(resData).map((key) => {
        const trip = resData[key];
        return {
          id: key,
          startTime: trip.startTime,
          endTime: trip.endTime,
          rawStartLat: trip.startLatitude,
          rawStartLng: trip.startLongitude,
          startLocation: renderMapLink(trip.startLatitude, trip.startLongitude, "View Start", "info"),
          rawEndLat: trip.endLatitude,
          rawEndLng: trip.endLongitude,
          endLocation: renderMapLink(trip.endLatitude, trip.endLongitude, "View End", "error"),
        };
      });

      setTripData(formattedTrips);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading trip report");
      openErrorSB();
    }
  };

  // Dispatch API Call based on Toggle
  const handleLoadData = async () => {
    if (!vehicleId) {
      setErrorMessage("Please select a vehicle first");
      openErrorSB();
      return;
    }
    setLoading(true);
    setErrorMessage("");
    
    if (viewType === "report") {
      await fetchReport();
    } else {
      await fetchTripReport();
    }
    
    setLoading(false);
  };

  // Combined CSV Export
  const exportToCSV = () => {
    let headers = [];
    let rows = [];
    let fileName = "";

    if (viewType === "report") {
      if (reportData.length === 0) return handleNoDataExport();
      headers = ["Date", "Ignition Status", "Start Time", "Start Lat", "Start Long", "End Time", "End Lat", "End Long", "Duration", "Distance"];
      rows = reportData.map((row) =>
        [row.onDate, row.ignitionStatus, row.ignStartTimeDate, row.rawStartLat, row.rawStartLng, row.ignEndTimeDate, row.rawEndLat, row.rawEndLng, row.ignChangeTime, row.totalDistance].join(",")
      );
      fileName = `IgnitionReport_${vehicleId}.csv`;
    } else {
      if (tripData.length === 0) return handleNoDataExport();
      headers = ["Trip ID", "Start Time", "Start Lat", "Start Long", "End Time", "End Lat", "End Long"];
      rows = tripData.map((row) =>
        [row.id, row.startTime, row.rawStartLat, row.rawStartLng, row.endTime, row.rawEndLat, row.rawEndLng].join(",")
      );
      fileName = `TripReport_${vehicleId}.csv`;
    }

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNoDataExport = () => {
    setErrorMessage("No data to export");
    openErrorSB();
  };

  const clearFilters = () => {
    setVehicleId("");
    setReportData([]);
    setTripData([]);
    setErrorMessage("");
    setStartTime(startTimeDefault);
    setEndTime(endTimeDefault);
  };

  // Check if current active view has data for CSV button disable state
  const hasData = viewType === "report" ? reportData.length > 0 : tripData.length > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0} display="flex" justifyContent="space-between" alignItems="center">
                {/* Toggle Group */}
                <ToggleButtonGroup
                  color="info"
                  value={viewType}
                  exclusive
                  onChange={(e, newView) => {
                    if (newView !== null) {
                      setViewType(newView);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="report">Ignition Report</ToggleButton>
                  <ToggleButton value="trip">Ignition Trip</ToggleButton>
                </ToggleButtonGroup>
              </MDBox>

              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Vehicle Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId || ""}
                        value={things.find((t) => t.thingId === vehicleId) || null}
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: "0.85rem" }}>
                            <strong>{option.nickName}</strong>
                            <span style={{ color: "#666", marginLeft: "10px" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Select Vehicle" fullWidth size="small" />
                        )}
                      />
                    </Grid>

                    {/* Start Time */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DateTimePicker
                        ampm={false}
                        label="Start Time"
                        value={startTime}
                        onChange={(newValue) => setStartTime(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>

                    {/* End Time */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DateTimePicker
                        ampm={false}
                        label="End Time"
                        value={endTime}
                        onChange={(newValue) => setEndTime(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12} md={3}>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <MDButton fullWidth variant="gradient" color="info" size="small" onClick={handleLoadData} disabled={loading}>
                            {loading ? <CircularProgress size={15} color="inherit" /> : "LOAD"}
                          </MDButton>
                        </Grid>
                        <Grid item xs={4}>
                          <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || !hasData} />
                        </Grid>
                        <Grid item xs={4}>
                          <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                            CLEAR
                          </MDButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={3} pt={2}>
                  <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <IgnitionReportsTable
                    table={{ 
                      columns: viewType === "report" ? reportColumns : tripColumns, 
                      rows: viewType === "report" ? reportData : tripData 
                    }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      
      {/* Snackbars */}
      <MDSnackbar color="success" icon="check" title="Success" content="Report generated successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} />
      <MDSnackbar color="error" icon="warning" title="Error" content={errorMessage || "Failed to fetch data"} open={errorSB} onClose={closeErrorSB} close={closeErrorSB} />
    </DashboardLayout>
  );
}

export default IgnitionReport;